/**
 * Export-time clip-path bridge for html2canvas.
 *
 * html2canvas v1.x has zero support for the CSS `clip-path` property —
 * converting `url(#…)` to native CSS expressions (`inset()`, `path()`, etc.)
 * doesn't help because the renderer ignores the property entirely.
 *
 * What this module does instead: for each clipped host that wraps an
 * `<img>` (the common Figma-style mask case), pre-render the image into
 * a `<canvas>` with the clipping baked in via canvas2d `clip()`. html2canvas
 * draws `<canvas>` elements directly via its CanvasElementContainer, so the
 * clipped output appears in the export.
 *
 * For hosts without an `<img>` child (text/shape masks, etc.), we drop the
 * CSS clip-path style — that's a no-op for html2canvas (it was ignoring it
 * anyway) but makes the intent explicit and avoids any chance of breaking
 * the visible content.
 *
 * The mask shapes inside `<clipPath>` defs are authored in element-local
 * coordinates by `CanvasEditor.getMaskShapeSVGContent`, so no coordinate
 * remapping is required between the SVG def and the canvas2d Path2D.
 */

const URL_REF_RE = /url\(["']?#([^"')]+)["']?\)/i;
const SVG_NS = 'http://www.w3.org/2000/svg';

function readSize(el) {
  const sw = parseFloat(el.style && el.style.width);
  const sh = parseFloat(el.style && el.style.height);
  if (Number.isFinite(sw) && sw > 0 && Number.isFinite(sh) && sh > 0) return [sw, sh];
  const rect = typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
  if (rect && rect.width > 0 && rect.height > 0) return [rect.width, rect.height];
  return [el.offsetWidth || 0, el.offsetHeight || 0];
}

// Parse one SVG `transform` attribute string into a DOMMatrix using the
// browser's native parser. Handles rotate/translate/scale/skew/matrix chains.
function svgTransformToMatrix(transformStr) {
  if (!transformStr || !transformStr.trim()) return new DOMMatrix();
  try {
    const svg = document.createElementNS(SVG_NS, 'svg');
    const g = document.createElementNS(SVG_NS, 'g');
    g.setAttribute('transform', transformStr);
    svg.appendChild(g);
    const list = g.transform.baseVal;
    if (!list || list.numberOfItems === 0) return new DOMMatrix();
    const cons = list.consolidate();
    if (!cons) return new DOMMatrix();
    const M = cons.matrix;
    return new DOMMatrix([M.a, M.b, M.c, M.d, M.e, M.f]);
  } catch {
    return new DOMMatrix();
  }
}

function isIdentity(M) {
  return M.a === 1 && M.b === 0 && M.c === 0 && M.d === 1 && M.e === 0 && M.f === 0;
}

// Build a Path2D from the first concrete shape inside a `<clipPath>` def.
// Descends through `<g transform="…">` wrappers, accumulating their transform,
// and applies it to the leaf shape via Path2D.addPath(sub, matrix).
function buildPath2DFromClipDef(def) {
  if (typeof Path2D === 'undefined') return null;
  const first = Array.from(def.children).find((c) => c.nodeType === 1);
  if (!first) return null;

  let M = new DOMMatrix();
  let n = first;
  let depth = 0;
  while (n && n.tagName && n.tagName.toLowerCase() === 'g') {
    if (++depth > 16) return null;
    const t = n.getAttribute('transform');
    if (t) M = M.multiply(svgTransformToMatrix(t));
    n = Array.from(n.children).find((c) => c.nodeType === 1);
    if (!n) return null;
  }

  const tag = n.tagName.toLowerCase();
  const sub = new Path2D();
  const num = (a, d = 0) => {
    const v = parseFloat(n.getAttribute(a));
    return Number.isFinite(v) ? v : d;
  };

  if (tag === 'rect') {
    const x = num('x'), y = num('y'), w = num('width'), h = num('height');
    const rx = num('rx');
    if (rx > 0) {
      const r = Math.min(rx, w / 2, h / 2);
      sub.moveTo(x + r, y);
      sub.lineTo(x + w - r, y);
      sub.quadraticCurveTo(x + w, y, x + w, y + r);
      sub.lineTo(x + w, y + h - r);
      sub.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      sub.lineTo(x + r, y + h);
      sub.quadraticCurveTo(x, y + h, x, y + h - r);
      sub.lineTo(x, y + r);
      sub.quadraticCurveTo(x, y, x + r, y);
      sub.closePath();
    } else {
      sub.rect(x, y, w, h);
    }
  } else if (tag === 'circle') {
    sub.arc(num('cx'), num('cy'), num('r'), 0, Math.PI * 2);
  } else if (tag === 'ellipse') {
    sub.ellipse(num('cx'), num('cy'), num('rx'), num('ry'), 0, 0, Math.PI * 2);
  } else if (tag === 'polygon' || tag === 'polyline') {
    const raw = (n.getAttribute('points') || '').trim();
    const pts = raw.split(/[\s,]+/).filter(Boolean).map(Number);
    if (pts.length < 4 || pts.length % 2 !== 0) return null;
    sub.moveTo(pts[0], pts[1]);
    for (let i = 2; i < pts.length; i += 2) sub.lineTo(pts[i], pts[i + 1]);
    if (tag === 'polygon') sub.closePath();
  } else if (tag === 'path') {
    const d = n.getAttribute('d');
    if (!d) return null;
    try {
      const raw = new Path2D(d);
      if (isIdentity(M)) return raw;
      const out = new Path2D();
      out.addPath(raw, M);
      return out;
    } catch {
      return null;
    }
  } else {
    return null;
  }

  if (isIdentity(M)) return sub;
  const out = new Path2D();
  out.addPath(sub, M);
  return out;
}

// Walk from a cloned descendant up to the cloned root, recording the child
// index at each step. The same index path through `liveRoot` lands on the
// element that was cloned. Returns null if the structure has diverged.
function findLiveCounterpart(clonedEl, liveRoot, clonedRoot) {
  if (!liveRoot || !clonedRoot) return null;
  const path = [];
  let n = clonedEl;
  while (n && n !== clonedRoot) {
    const parent = n.parentNode;
    if (!parent) return null;
    const idx = Array.prototype.indexOf.call(parent.children, n);
    if (idx < 0) return null;
    path.unshift(idx);
    n = parent;
  }
  if (n !== clonedRoot) return null;
  let live = liveRoot;
  for (const i of path) {
    if (!live || !live.children || i >= live.children.length) return null;
    live = live.children[i];
  }
  return live;
}

// Pre-render the masked image into a fresh <canvas>, clipping via Path2D.
// Returns null when the source img isn't loadable or the path isn't supported
// — caller falls back to dropping the clip-path style.
function preRenderClippedImage({ def, w, h, doc, liveHost }) {
  if (!liveHost) return null;
  const liveImg = liveHost.querySelector('img');
  if (!liveImg || !liveImg.complete || !liveImg.naturalWidth) return null;

  const path = buildPath2DFromClipDef(def);
  if (!path) return null;

  // Match the html2canvas downstream scale (DownloadDialog uses scale=2).
  const scale = 2;
  const canvas = doc.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(w * scale));
  canvas.height = Math.max(1, Math.ceil(h * scale));
  canvas.style.position = 'absolute';
  canvas.style.left = '0';
  canvas.style.top = '0';
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  canvas.style.pointerEvents = 'none';

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(scale, scale);
  ctx.save();
  ctx.clip(path);

  // Replicate the live <img>'s `object-fit: cover` positioning so the
  // exported pixels match what the user sees in the editor.
  const iw = liveImg.naturalWidth;
  const ih = liveImg.naturalHeight;
  const cover = Math.max(w / iw, h / ih);
  const dw = iw * cover;
  const dh = ih * cover;
  const dx = (w - dw) / 2;
  const dy = (h - dh) / 2;

  try {
    ctx.drawImage(liveImg, dx, dy, dw, dh);
  } catch {
    // Tainted by cross-origin image without CORS — caller falls back.
    return null;
  }
  ctx.restore();
  return canvas;
}

/**
 * Rewrite each cloned `clip-path: url(#…)` host so html2canvas can render
 * the clipping correctly.
 *
 * @param {Document | HTMLElement} root - cloned canvas (the only tree this
 *   function mutates).
 * @param {HTMLElement} [liveRoot] - the live canvas. Used to source loaded
 *   `<img>` bitmaps and naturalWidth/Height for canvas pre-rendering.
 * @returns {Array<{reason: string, clipId?: string, host?: string}>}
 *   list of failures (empty when every clip-path was successfully handled).
 */
export function inlineClipPathsForExport(root, liveRoot) {
  const failures = [];
  if (!root) return failures;
  const doc = root.ownerDocument || (root.nodeType === 9 ? root : null);
  if (!doc) return failures;

  const scope = root.querySelectorAll ? root : doc;

  // Index every <clipPath id> def in the cloned subtree.
  const defs = new Map();
  scope.querySelectorAll('clipPath[id]').forEach((cp) => {
    const id = cp.getAttribute('id');
    if (id && !defs.has(id)) defs.set(id, cp);
  });

  // Snapshot hosts before mutating — replacing children invalidates live lists.
  const hosts = [];
  scope.querySelectorAll('*').forEach((el) => {
    if (el.nodeType !== 1 || !el.style) return;
    // SVG elements honour the clip-path attribute natively — leave them alone.
    if (el.namespaceURI === SVG_NS) return;
    const inline = el.style.clipPath || el.style.webkitClipPath || '';
    if (URL_REF_RE.test(inline)) hosts.push(el);
  });

  for (const el of hosts) {
    const inline = el.style.clipPath || el.style.webkitClipPath || '';
    const m = URL_REF_RE.exec(inline);
    if (!m) continue;
    const clipId = m[1];
    const def = defs.get(clipId);
    if (!def) {
      failures.push({ reason: 'clipPath def not found', clipId, host: el.tagName });
      // Drop the dangling reference so it doesn't confuse downstream tooling.
      el.style.clipPath = '';
      el.style.webkitClipPath = '';
      continue;
    }

    const [w, h] = readSize(el);
    if (w <= 0 || h <= 0) {
      failures.push({ reason: 'invalid element size', clipId, host: el.tagName });
      el.style.clipPath = '';
      el.style.webkitClipPath = '';
      continue;
    }

    const liveHost = findLiveCounterpart(el, liveRoot, root);
    const canvas = preRenderClippedImage({ def, w, h, doc, liveHost });

    if (canvas) {
      // Drop the host's existing children (img, def svg, eraser-mask svg,
      // resize handle stubs) — the pre-rendered canvas replaces them with
      // clipping baked in.
      while (el.firstChild) el.removeChild(el.firstChild);
      el.appendChild(canvas);
    } else if (liveHost && liveHost.querySelector('img')) {
      // We have an image but couldn't pre-render it (Path2D unsupported,
      // tainted canvas, etc.). Record it; output will be unclipped.
      failures.push({ reason: 'canvas pre-render failed', clipId, host: el.tagName });
    }

    // Always strip the CSS clip-path: html2canvas ignores it regardless, and
    // leaving it referencing the no-longer-relevant <clipPath> def is just
    // noise.
    el.style.clipPath = '';
    el.style.webkitClipPath = '';
  }

  return failures;
}
